<template>
  <v-dialog
    v-model="isVisible"
    max-width="800"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title>
        Docket Entry Notifications Editor
      </v-card-title>
      <v-divider />
      <v-card-text>
        <DocketEntryAttendeeNotifications
          v-model="notifications"
          :docket-entry="docketEntry"
          :legal-case="legalCase"
          :notifications="originalNotifications"
          :parties="availableParties"
          :valid.sync="isValid"
        />
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          text
          @click="_ => closeDialog()"
        >
          Cancel
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="!isValid"
          @click="_ => saveNotificationsAndClose()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { cloneDeep } from 'lodash'

import { DOCKET_ENTRY_TYPE_NAMESPACE } from '@/store/docket-entry-type'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { RECORD_NAMESPACE } from '@/store/record'

import DocketEntryAttendeeNotifications from './DocketEntryAttendeeNotifications.vue'

export default {
  name: 'DocketEntryNotificationsEditorDialog',

  components: {
    DocketEntryAttendeeNotifications,
  },

  model: {
    prop: 'visible',
    event: 'update',
  },

  props: {
    docketEntry: {
      type: Object,
      required: true,
    },

    parties: {
      type: Array,
      default: _ => ([]),
    },

    // eslint-disable-next-line vue/no-unused-properties
    valid: {
      type: Boolean,
      default: false,
    },

    visible: {
      type: Boolean,
      required: true,
    },
  },

  data () {
    return {
      isValid: false,
      notifications: [],
      record: null,
    }
  },

  computed: {
    availableParties () {
      let parties = this.docketEntry?.attendances?.reduce((res, att) => {
        const party = this.legalCaseParties.find(p => p.id === att.party_id)
        if (party) res.push(party)
        return res
      }, [])

      if (!parties?.length && this.legalCaseParties?.length) {
        parties = []

        if (this.record?.record_holder_party) {
          parties.push({
            ...this.record?.record_holder_party,
            id: this.record?.record_holder_party_id,
          })
        }

        if (this.record?.contact_party) {
          parties.push({
            ...this.record?.contact_party,
            id: this.record?.contact_party_id,
          })
        }

        parties = parties.concat(this.legalCaseParties.reduce((res, party) => {
          if (!res.find(p => p.id === party.id)) res.push(party)
          return res
        }, []))
      }

      return parties
    },

    docketEntryType () {
      return this.docketEntryTypes.find(det => det.id === this.docketEntry.docket_entry_type_id)
    },

    isVisible: {
      get () { return this.visible },
      set (value) { this.$emit('update', value) },
    },

    legalCase () {
      return this.legalCases.find(lc => lc.id === this.docketEntry.legal_case_id)
    },

    legalCaseParties () {
      return this.legalCase?.parties ?? this.parties
    },

    originalNotifications () {
      return this.docketEntry?.docket_entry_notifications ?? []
    },

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'legalCases',
    ]),

    ...mapGetters(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'docketEntryTypes',
    ]),

    ...mapGetters(RECORD_NAMESPACE, [
      'currentRecord',
    ]),
  },

  watch: {
    isValid: {
      handler (value) {
        this.$emit('update:valid', value)
      },

      immediate: true,
    },

    legalCase: {
      handler (value) {
        if (!value && !this.currentRecord) {
          this.record = null
          return
        }

        if (value && value.record_id === this.record?.id) return
        if (!value && this.currentRecord.id === this.record?.id) return

        if (value) this.getRecordById(value?.record_id).then(data => { this.record = data })
        else this.record = cloneDeep(this.currentRecord)
      },

      immediate: true,
    },
  },

  methods: {
    closeDialog () {
      this.isVisible = false
    },

    saveNotificationsAndClose () {
      this.$emit('save', this.notifications)
      this.isVisible = false
    },

    ...mapActions(RECORD_NAMESPACE, [
      'getRecordById',
    ]),
  },
}
</script>
